import _ from "lodash";
import { CSSProperties, FC, useEffect, useState } from 'react';
import { 
  MenuItem, FormControl, InputLabel, Box, Typography,
  Select, TextField, FormHelperText, Grid, Divider
} from "@mui/material"

import { Controller, useFormContext } from "react-hook-form"
import { useTranslation } from "react-i18next";
import { NumericFormatField } from "@gisce/oficina-virtual-components";
import { FieldRenderProps } from "react-final-form";
import { useTipovias } from "@/components/form-fields/AddressForm/hooks/useTipovias";
import { usePoblacionsINE } from "./hooks/usePoblacionsINE";
import { useProvincies } from "./hooks/useProvincies";

interface IAddressForm {
  readOnly?: boolean;
  showMobile?: boolean;
  showEmail?: boolean;
  showName?: boolean;
  label?: JSX.Element;
  name?: string;
  style?: CSSProperties;
  input?: FieldRenderProps<unknown>['input'];
}

const AddressForm: FC<IAddressForm> = ({
  readOnly = false,
  label = '',
  showMobile = false,
  showEmail = false,
  // showName = false
  name = "address",
  style = {},
  input
}) => {
  const { t } = useTranslation();

  const { 
    control,
    watch,
    setValue,
    trigger
  } = useFormContext();

  const watchAddress = watch(name);

  const tipoviasData = useTipovias();
  const poblacionsINEData = usePoblacionsINE(watchAddress.dp);
  const provinciesData = useProvincies(watchAddress.ine.slice(0, 2));

  // Població
  useEffect(() => {
    if (poblacionsINEData.isSuccess && poblacionsINEData.data.length == 0) {
      setValue(`${name}.poblacio`, '');
      setValue(`${name}.state`, '');
    }
  }, [watchAddress?.dp])

  // Població
  useEffect(() => {
    if (poblacionsINEData.isSuccess && poblacionsINEData.data.length == 1) {
      setValue(`${name}.poblacio`, poblacionsINEData.data[0].city);
      trigger(`${name}.poblacio`)
    }
  }, [poblacionsINEData.isSuccess, poblacionsINEData.data])

  // State
  useEffect(() => {
    if (watchAddress?.poblacio && provinciesData.isSuccess && provinciesData.data.length == 1) {
      setValue(`${name}.state`, provinciesData.data[0].id);
      trigger(`${name}.state`)
    } else {
      setValue(`${name}.state`, '');
    }
  }, [provinciesData.isSuccess, provinciesData.data, watchAddress?.poblacio])

  // INE
  useEffect(() => {
    if (watchAddress?.poblacio && poblacionsINEData.isSuccess && poblacionsINEData.data.length) {
      const newINE = poblacionsINEData.data.find(pobINE => pobINE.city == watchAddress.poblacio)?.ine ?? "";
      setValue(`${name}.ine`, newINE);
    }
  }, [watchAddress?.poblacio]);

  useEffect(() => {
    if (typeof input?.onChange === "function") {
      input.onChange(watchAddress);
    }

  }, Object.values(watchAddress));

  const renderAddress = () => {

    if (_.isEmpty(watchAddress)) {
      return <div/>
    }

    return (
      <div>
        <div>{tipoviasData.isSuccess && tipoviasData.data.find(tv => tv.id == watchAddress.tv)?.name } { watchAddress.nv }, { watchAddress.pnp } {watchAddress.bq} {watchAddress.es} {watchAddress.pt} {watchAddress.pu}</div>
        <div>{watchAddress.dp} { watchAddress.poblacio } ({provinciesData.isSuccess && provinciesData.data.find(prov => prov.id == watchAddress.state)?.name})</div>
        { watchAddress.ref_catastral &&
          <div>{ t('common:text.address_ref') } { watchAddress.ref_catastral }</div>
        }
      </div>
    );
  }

  const renderForm = () => {

    const possiblePoblacionsINE = poblacionsINEData.isSuccess && poblacionsINEData.data.map((pob, idx) => {
      return <MenuItem key={idx} value={pob.city}>{pob.city}</MenuItem>
    });

    const possibleTipovias = tipoviasData.isSuccess && tipoviasData.data.map((tipovia, idx) => {
      return <MenuItem key={idx} value={tipovia.id}>{tipovia.name}</MenuItem>
    });

    return (
      <div>
        {(showMobile || showEmail) &&
          <>
            {!label &&
              <Typography variant="subtitle1" style={{marginTop: 20}}>
                {t('common:text.contractation_contact_details')}
              </Typography>
            }
            <Grid container spacing={3}>
              {showMobile && (
                <Grid item xs={12} sm={6} >
                  <Controller
                    name={`${name}.mobile`}
                    control={control}
                    render={({ field, fieldState }) => (<>
                      <TextField
                        {...field}
                        label={t('common:text.address_phone')}
                        style={{ width: "100%" }}
                        error={!!(fieldState.error)}
                      />
                      {fieldState.error && (
                        <FormHelperText error={true}>
                          {fieldState.error.message}
                        </FormHelperText>
                      )}
                    </>)}
                  />
                </Grid>
              )}
              {showEmail && (
                <Grid item xs={12} sm={6}>
                  <Controller
                    name={`${name}.email`}
                    control={control}
                    render={({ field, fieldState }) => (<>
                      <TextField
                        {...field}
                        label={t('common:text.address_email')}
                        style={{ width: "100%" }}
                        error={!!(fieldState.error)}
                      />
                      {fieldState.error && (
                        <FormHelperText error={true}>
                          {fieldState.error.message}
                        </FormHelperText>
                      )}
                    </>)}
                  />
                </Grid>
              )}
            </Grid>
          </>
        }
        {/* {showName && (
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('common:text.address_name')}
                name="name"
                fullWidth
                onChange={handleGenericOnChange}
                value={address?.name}
                error={!!errors?.name}
              />
              {errors?.name && (
                <FormHelperText error={true}>{errors.name}</FormHelperText>
              )}
            </Grid>
          </Grid>
        )} */}
        <Divider style={{marginBottom: 20, marginTop: 20}}/>
        { !label &&
        <Typography variant="subtitle1">
          {t('common:text.contractation_address')}
        </Typography>
        }
        <Grid container spacing={3}>
          <Grid item xs={12} sm={4} md={2}>
            <Controller
              name={`${name}.dp`}
              control={control}
              render={({ field, fieldState }) => (<>
                <TextField
                  {...field}
                  InputProps={{
                    inputComponent: NumericFormatField,
                    inputProps: {
                      allowNegative: false,
                      allowLeadingZeros: true,
                      decimalScale: 0
                    }
                  }}
                  label={t('common:text.address_postal_code')}
                  style={{ width: "100%" }}
                  error={!!(fieldState.error)}
                />
                {fieldState.error && (
                  <FormHelperText error={true}>
                    {fieldState.error.message}
                  </FormHelperText>
                )}
              </>)}
            />
          </Grid>
          <Grid item xs={12} sm={8} md={5}>
            <Controller
              name={`${name}.poblacio`}
              control={control}
              render={({field, fieldState}) => (<FormControl
                  style={{ width: "100%" }}
                >
                  <InputLabel shrink htmlFor="">
                    {t('common:text.address_locality')}
                  </InputLabel>

                  <Select
                    {...field}
                    disabled={!watchAddress.dp}
                    error={!!(fieldState.error)}
                    autoWidth={true}
                  >
                    {possiblePoblacionsINE}
                  </Select>

                  {fieldState.error && (
                    <FormHelperText error={true}>
                      {fieldState.error.message}
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={5}>
            <Controller
              name={`${name}.state`}
              control={control}
              render={({field, fieldState}) => (<FormControl
                  style={{ width: "100%" }}
                >
                  <InputLabel shrink htmlFor="">
                    {t('common:text.address_province')}
                  </InputLabel>

                  <Select
                    {...field}
                    disabled={true}
                    autoWidth={true}
                    error={!!(fieldState.error)}
                  >
                    {provinciesData.isSuccess && 
                      provinciesData.data.map((prov, idx) => <MenuItem
                        key={idx}
                        value={prov.id}>
                          {prov.name}
                      </MenuItem>
                    )}
                  </Select>

                  {fieldState.error && (
                    <FormHelperText error={true}>
                      {fieldState.error.message}
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Controller
              name={`${name}.tv`}
              control={control}
              render={({field, fieldState}) => (<FormControl
                  style={{ width: "100%" }}
                >
                  <InputLabel shrink htmlFor="">
                    {t('common:text.address_street_type')}
                  </InputLabel>

                  <Select
                    {...field}
                    disabled={tipoviasData.isLoading || tipoviasData.isError}
                    error={!!(fieldState.error)}
                    autoWidth={true}
                  >
                    {possibleTipovias}
                  </Select>

                  {fieldState.error && (
                    <FormHelperText error={true}>
                      {fieldState.error.message}
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            />
          </Grid>
          <Grid item xs={12} sm={10} md={7}>
            <Controller
              name={`${name}.nv`}
              control={control}
              render={({ field, fieldState }) => (<>
                <TextField
                  {...field}
                  label={t('common:text.address_street_name')}
                  style={{ width: "100%" }}
                  error={!!(fieldState.error)}
                />
                {fieldState.error && (
                  <FormHelperText error={true}>
                    {fieldState.error.message}
                  </FormHelperText>
                )}
              </>)}
            />
          </Grid>
          <Grid item xs={6} sm={2}>
            <Controller
              name={`${name}.pnp`}
              control={control}
              render={({ field, fieldState }) => (<>
                <TextField
                  {...field}
                  label={t('common:text.address_street_number')}
                  style={{ width: "100%" }}
                  error={!!(fieldState.error)}
                />
                {fieldState.error && (
                  <FormHelperText error={true}>
                    {fieldState.error.message}
                  </FormHelperText>
                )}
              </>)}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <Controller
              name={`${name}.bq`}
              control={control}
              render={({ field, fieldState }) => (<>
                <TextField
                  {...field}
                  label={t('common:text.address_block')}
                  style={{ width: "100%" }}
                  error={!!(fieldState.error)}
                />
                {fieldState.error && (
                  <FormHelperText error={true}>
                    {fieldState.error.message}
                  </FormHelperText>
                )}
              </>)}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <Controller
              name={`${name}.es`}
              control={control}
              render={({ field, fieldState }) => (<>
                <TextField
                  {...field}
                  label={t('common:text.address_stairs')}
                  style={{ width: "100%" }}
                  error={!!(fieldState.error)}
                />
                {fieldState.error && (
                  <FormHelperText error={true}>
                    {fieldState.error.message}
                  </FormHelperText>
                )}
              </>)}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <Controller
              name={`${name}.pt`}
              control={control}
              render={({ field, fieldState }) => (<>
                <TextField
                  {...field}
                  label={t('common:text.address_floor')}
                  style={{ width: "100%" }}
                  error={!!(fieldState.error)}
                />
                {fieldState.error && (
                  <FormHelperText error={true}>
                    {fieldState.error.message}
                  </FormHelperText>
                )}
              </>)}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <Controller
              name={`${name}.pu`}
              control={control}
              render={({ field, fieldState }) => (<>
                <TextField
                  {...field}
                  label={t('common:text.address_door')}
                  style={{ width: "100%" }}
                  error={!!(fieldState.error)}
                />
                {fieldState.error && (
                  <FormHelperText error={true}>
                    {fieldState.error.message}
                  </FormHelperText>
                )}
              </>)}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name={`${name}.ref_catastral`}
              control={control}
              render={({ field, fieldState }) => (<>
                <TextField
                  {...field}
                  onChange={(event) => {
                    field.onChange(
                      event.target.value.replace(/\s/g, "").toUpperCase()
                    )
                  }}
                  label={t('common:text.address_ref')}
                  style={{ width: "100%" }}
                  error={!!(fieldState.error)}
                />
                {fieldState.error && (
                  <FormHelperText error={true}>
                    {fieldState.error.message}
                  </FormHelperText>
                )}
              </>)}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name={`${name}.aclarador`}
              control={control}
              render={({ field, fieldState }) => (<>
                <TextField
                  {...field}
                  label={t('common:text.address_hint')}
                  style={{ width: "100%" }}
                  error={!!(fieldState.error)}
                  variant="filled"
                  minRows={2}
                  multiline
                />
                {fieldState.error && (
                  <FormHelperText error={true}>
                    {fieldState.error.message}
                  </FormHelperText>
                )}
              </>)}
            />
            <div
              className="helper-text" 
              style={{ width: "100%", marginTop: 5 }}
            >
              {t('common:text.address_hint_helper')}
            </div>
          </Grid>
        </Grid>
      </div>
    );
  }

  return (
    <div style={style}>
      {label &&
        <Box>
          <Typography variant="h6">
            {label}
          </Typography>
        </Box>
      }
      {!readOnly && renderForm()}
      {readOnly && renderAddress()}

    </div>
  );
}

export default AddressForm;